/**
 * Wishlist Drawer Component
 * =========================
 * A slide-out drawer for quick wishlist access, following the cart-drawer pattern.
 * Integrates with WishlistKing API for real-time wishlist management.
 */

class WishlistDrawer extends DrawerComponent {
  constructor() {
    super();
    this.initialized = false;
    this.wishlistItems = [];
    this.wishlistId = null;
    this.hasRoomPreviews = false;
    this.projects = [];
    this.projectVariantIds = new Set();
    this.roomPreviewObjectUrls = [];
    this.onWishlistUpdateListener = this.onWishlistUpdate.bind(this);
    this.onWlkUpdateListener = this.onWlkUpdate.bind(this);
    this.onRoomUpdateListener = this.onRoomUpdate.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get emptyState() {
    return this.querySelector('[data-wishlist-empty]');
  }

  get bodyContainer() {
    return this.querySelector('[data-wishlist-body]');
  }

  get itemsContainer() {
    return this.querySelector('[data-wishlist-items]');
  }

  get footer() {
    return this.querySelector('[data-wishlist-footer]');
  }

  get countElements() {
    return this.querySelectorAll('[data-wishlist-count-number]');
  }

  get disclaimer() {
    return this.querySelector('[data-wishlist-room-disclaimer]');
  }

  get signInPrompt() {
    return this.querySelector('[data-wishlist-sign-in]');
  }

  get signedIn() {
    return this.dataset.customerSignedIn === 'true';
  }

  get roomPreviewLabels() {
    return {
      remove: this.dataset.roomPreviewRemoveLabel || 'Remove',
      addToCart: this.dataset.roomPreviewAddToCartLabel || 'Add to cart',
      cartAdded: this.dataset.roomPreviewCartAddedLabel || 'Added to cart',
      badge: this.dataset.roomPreviewBadgeLabel || 'AI preview',
      actionError: this.dataset.roomPreviewActionErrorLabel || 'Couldn\'t complete that action. Try again.',
    };
  }

  connectedCallback() {
    super.connectedCallback();

    // Listen for wishlist updates from wishlist-custom.js
    document.addEventListener('wishlist:updated', this.onWishlistUpdateListener);

    // Listen for WishlistKing native events
    document.addEventListener('wlk:wishlist:updated', this.onWlkUpdateListener);
    document.addEventListener('wlk:wishlist:added', this.onWlkUpdateListener);
    document.addEventListener('wlk:wishlist:removed', this.onWlkUpdateListener);

    // Listen for room-preview changes (view-in-room feature, browser-local IndexedDB)
    document.addEventListener('room:updated', this.onRoomUpdateListener);

    // Initialize on first open
    this.addEventListener('drawer:handleAfterShow', () => {
      if (!this.initialized) {
        this.initialize();
      }
    });

    // Bind remove and add-to-cart handlers
    this.addEventListener('click', this.handleClick.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wishlist:updated', this.onWishlistUpdateListener);
    document.removeEventListener('wlk:wishlist:updated', this.onWlkUpdateListener);
    document.removeEventListener('wlk:wishlist:added', this.onWlkUpdateListener);
    document.removeEventListener('wlk:wishlist:removed', this.onWlkUpdateListener);
    document.removeEventListener('room:updated', this.onRoomUpdateListener);
    this.roomPreviewObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.roomPreviewObjectUrls = [];
  }

  async initialize() {
    // Prevent double initialization
    if (this.isLoading) return;
    this.isLoading = true;

    await this.waitForWishlistKing();
    if (this.signedIn) window.LampsRoomStudio?.claimProjects?.();
    await this.loadProjects();
    await this.loadWishlistItems();
    this.initialized = true;
    this.isLoading = false;
    if (this.roomUpdatePending) {
      this.roomUpdatePending = false;
      await this.onRoomUpdate();
    }
  }

  waitForWishlistKing() {
    return new Promise((resolve) => {
      if (window.WishlistKing) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 100;

      const check = () => {
        attempts++;
        if (window.WishlistKing) {
          resolve();
        } else if (attempts < maxAttempts) {
          setTimeout(check, 100);
        } else {
          console.warn('[WishlistDrawer] WishlistKing not available after 10s');
          resolve();
        }
      };

      check();
    });
  }

  async loadWishlistItems() {
    if (!window.WishlistKing) {
      this.showEmpty();
      return;
    }

    try {
      const wishlist = await this.getWishlist();

      if (!wishlist?.items?.length) {
        this.wishlistItems = [];
        this.wishlistId = null;
        this.showEmpty();
        return;
      }

      this.wishlistItems = wishlist.items;
      // Store the wishlist ID for sharing (publicId is the shareable ID)
      this.wishlistId = wishlist.publicId || wishlist.id || wishlist._id;
      await this.renderItems();
    } catch (err) {
      console.error('[WishlistDrawer] Failed to load wishlist:', err);
      this.showEmpty();
    }
  }

  getWishlist() {
    return new Promise((resolve) => {
      // Declared before subscribing: an observable holding cached state emits during the
      // subscribe() call itself, which would reach this binding before assignment.
      const handle = {};
      let settled = false;
      handle.subscription = window.WishlistKing.state
        .observeWishlist({ wishlistId: 'mine' })
        .subscribe((wishlist) => {
          settled = true;
          handle.subscription?.unsubscribe();
          resolve(wishlist);
        });
      if (settled) handle.subscription?.unsubscribe();
    });
  }

  async renderItems() {
    if (!this.wishlistItems.length) {
      this.showEmpty();
      return;
    }

    // Get unique product IDs for the search query
    const uniqueProductIds = [...new Set(
      this.wishlistItems
        .filter((item) => item.product?.id)
        .map((item) => item.product.id)
    )];

    if (!uniqueProductIds.length) {
      this.showEmpty();
      return;
    }

    try {
      // Project cards are cloned from the same section, so their products go in the same
      // request rather than a second round trip.
      const productCardMap = await this.fetchProductCards([...uniqueProductIds, ...this.projectProductIds()]);

      if (Object.keys(productCardMap).length > 0) {
        // Clear container and render one card per wishlist item
        this.itemsContainer.innerHTML = '';

        // Hide items during update to prevent layout shift
        this.itemsContainer.style.opacity = '0';

        for (const item of this.wishlistItems) {
          const productId = item.product?.id;
          const templateCard = productCardMap[productId];
          // A product already showing as a room project is one row, not two. The project
          // card wins: it carries the shopper's own photo.
          if (this.projectVariantIds.has(String(item.variant?.id || item.variantId || ''))) continue;

          if (templateCard) {
            const card = templateCard.cloneNode(true);
            // Store wishlist item ID for removal
            card.dataset.wishlistItemId = item.id;
            // Store variant ID if present (can be in item.variant.id or item.variantId)
            const variantId = item.variant?.id || item.variantId;
            if (variantId) {
              card.dataset.variantId = variantId;
            }
            this.itemsContainer.appendChild(card);
          }
        }

        this.renderProjectCards(productCardMap);

        // Update cards with specific variant data
        await this.updateCardsWithVariants();

        this.updateCount(this.itemCount());

        // Show body container first (it's hidden by default), then fade in items
        this.showBody();

        // Small delay to ensure body is visible before fading in items
        requestAnimationFrame(() => {
          this.itemsContainer.style.opacity = '1';
        });
      } else {
        this.showEmpty();
      }
    } catch (err) {
      console.error('[WishlistDrawer] Failed to render items:', err);
      this.showEmpty();
    }
  }

  projectProductIds() {
    return this.projects.map((record) => record?.product?.id).filter(Boolean);
  }

  // One card shape for the drawer: the theme renders it in Liquid, with the vendor, the
  // option list and the sale/compare prices that hand-built markup kept missing.
  async fetchProductCards(productIds) {
    const unique = [...new Set(productIds.filter(Boolean).map(String))];
    if (!unique.length) return {};

    const query = unique.map((id) => `id:${id}`).join(' OR ');
    const searchUrl = `${FoxTheme.routes.root_url}search?section_id=wishlist-drawer-cards&type=product&q=${encodeURIComponent(query)}`;
    const response = await fetch(searchUrl);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cardsContainer = doc.querySelector('[data-wishlist-drawer-cards]');

    const productCardMap = {};
    if (cardsContainer) {
      Array.from(cardsContainer.children).forEach((card) => {
        if (card.dataset.productId) productCardMap[card.dataset.productId] = card;
      });
    }
    return productCardMap;
  }

  /**
   * Update rendered cards with specific variant information
   * Each card now has a data-wishlist-item-id and optionally data-variant-id
   */
  async updateCardsWithVariants() {
    const cards = Array.from(this.itemsContainer.querySelectorAll('[data-variant-id]'));

    // Cache product data to avoid duplicate fetches for same product
    const productDataCache = {};

    for (const card of cards) {
      const variantId = card.dataset.variantId;

      if (!variantId) continue;

      // Update add-to-cart button with correct variant
      const addToCartBtn = card.querySelector('[data-wishlist-add-to-cart], [data-room-preview-add-to-cart]');
      if (addToCartBtn) {
        addToCartBtn.dataset.variantId = variantId;
      }

      // Fetch variant-specific data for price and details
      try {
        const productHandle = card.dataset.productHandle;

        // Use cached product data if available
        if (!productDataCache[productHandle]) {
          const variantResponse = await fetch(
            `${FoxTheme.routes.root_url}products/${productHandle}.js`
          );
          productDataCache[productHandle] = await variantResponse.json();
        }

        const productData = productDataCache[productHandle];
        const variant = productData.variants.find((v) => v.id === parseInt(variantId, 10));

        if (variant) {
          // Update variant image if available
          if (variant.featured_image) {
            const imageElement = card.querySelector('.wishlist-drawer-item__image');
            if (imageElement) {
              imageElement.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_200x$1');
              imageElement.alt = variant.featured_image.alt || variant.name;
            }
          }

          // Update SKU. Prefer the per-variant vendor_sku metafield emitted by
          // wishlist-drawer-cards.liquid as data-variant-skus (matches cart-drawer
          // convention at sections/cart-drawer.liquid:316). products.js doesn't
          // carry metafields, so we cannot derive vendor_sku from `variant` here —
          // fall back to variant.sku only if the map is unavailable or missing
          // the entry.
          let displaySku = variant.sku;
          try {
            const variantSkusRaw = card.dataset.variantSkus;
            if (variantSkusRaw) {
              const variantSkus = JSON.parse(variantSkusRaw);
              const mapped = variantSkus[String(variantId)] || variantSkus[variantId];
              if (mapped) {
                displaySku = mapped;
              }
            }
          } catch (e) {
            console.warn('[WishlistDrawer] Failed to parse variant SKU map:', e);
          }

          const skuElement = card.querySelector('.wishlist-drawer-item__sku');
          if (displaySku) {
            if (skuElement) {
              skuElement.textContent = displaySku;
            } else {
              // Create SKU element if it doesn't exist
              const detailsGrid = card.querySelector('.wishlist-drawer-item__details .grid.flex-1');
              if (detailsGrid) {
                const titleDiv = detailsGrid.querySelector('.block');
                if (titleDiv) {
                  const skuP = document.createElement('p');
                  skuP.className = 'wishlist-drawer-item__sku text-subtext text-xs m-0';
                  skuP.textContent = displaySku;
                  titleDiv.after(skuP);
                }
              }
            }
          }

          // Add variant options display with labels
          // First, always check for and clear any existing options container from cloned cards
          const existingOptionsContainer = card.querySelector('.wishlist-drawer-item__options');
          if (existingOptionsContainer) {
            existingOptionsContainer.remove();
          }

          if (variant.options && variant.options.length > 0 && productData.options) {
            // Check for V2 variant configuration with dimension labels
            const variantConfigStr = card.dataset.variantConfig;
            let variantConfig = null;
            if (variantConfigStr) {
              try {
                variantConfig = JSON.parse(variantConfigStr);
              } catch (e) {
                console.warn('[WishlistDrawer] Failed to parse variant config:', e);
              }
            }

            const optionsHtml = productData.options
              .map((option, index) => {
                const optionName = typeof option === 'string' ? option : (option.name || '');
                const optionValue = variant.options[index];
                // Skip "Default Title" (single variant with no real options)
                if (!optionValue || optionValue === 'Default Title') {
                  return '';
                }
                // For generic option names ("Title", "Variant") with compound values (e.g., "Gold/White/Large"),
                // use V2 variant_configuration dimension labels if available
                const isGenericOptionName = optionName === 'Title' || optionName === 'Variant';
                if (isGenericOptionName && optionValue.includes('/')) {
                  const parts = optionValue.split('/');
                  const dimensions = variantConfig?.dimensions || [];

                  // Only show parts that have corresponding dimension labels
                  // This prevents showing extra unlabeled parts from compound option values
                  const partsToShow = dimensions.length > 0 ? parts.slice(0, dimensions.length) : parts;

                  return partsToShow.map((part, i) => {
                    const label = dimensions[i]?.label;
                    if (label) {
                      return `<li class="wishlist-drawer-item__option text-xs"><span class="wishlist-drawer-item__option-label">${label}:</span> ${part.trim()}</li>`;
                    }
                    return `<li class="wishlist-drawer-item__option text-xs">${part.trim()}</li>`;
                  }).join('');
                }
                // For generic option names without "/", use first dimension label if available
                if (isGenericOptionName) {
                  const dimensions = variantConfig?.dimensions || [];
                  const label = dimensions[0]?.label;
                  if (label) {
                    return `<li class="wishlist-drawer-item__option text-xs"><span class="wishlist-drawer-item__option-label">${label}:</span> ${optionValue}</li>`;
                  }
                  return `<li class="wishlist-drawer-item__option text-xs">${optionValue}</li>`;
                }
                // Show label: value for named options
                return `<li class="wishlist-drawer-item__option text-xs"><span class="wishlist-drawer-item__option-label">${optionName}:</span> ${optionValue}</li>`;
              })
              .filter(Boolean)
              .join('');

            // Create options container only if there's content to show
            if (optionsHtml) {
              const detailsGrid = card.querySelector('.wishlist-drawer-item__details .grid.flex-1');
              if (detailsGrid) {
                const optionsContainer = document.createElement('ul');
                optionsContainer.className = 'wishlist-drawer-item__options list-none m-0 p-0 mt-1';
                optionsContainer.innerHTML = optionsHtml;
                // Insert after vendor or SKU
                const vendor = detailsGrid.querySelector('.wishlist-drawer-item__vendor');
                const sku = detailsGrid.querySelector('.wishlist-drawer-item__sku');
                const insertAfter = vendor || sku || detailsGrid.querySelector('.block');
                if (insertAfter) {
                  insertAfter.after(optionsContainer);
                }
              }
            }
          }

          // Update price display
          const pricesContainer = card.querySelector('.wishlist-drawer-item__prices');
          if (pricesContainer) {
            const moneyFormat = FoxTheme.settings?.moneyFormat || '${{amount}}';
            const formattedPrice = FoxTheme.Currency.formatMoney(variant.price, moneyFormat);
            const formattedComparePrice = variant.compare_at_price
              ? FoxTheme.Currency.formatMoney(variant.compare_at_price, moneyFormat)
              : null;

            if (formattedComparePrice && variant.compare_at_price > variant.price) {
              pricesContainer.innerHTML = `
                <span class="wishlist-drawer-item__price-sale">${formattedPrice}</span>
                <span class="wishlist-drawer-item__price-compare">${formattedComparePrice}</span>
              `;
            } else {
              pricesContainer.innerHTML = `
                <span class="wishlist-drawer-item__price-regular">${formattedPrice}</span>
              `;
            }
          }

          // Update availability
          if (!variant.available) {
            const addToCartBtn = card.querySelector('[data-wishlist-add-to-cart], [data-room-preview-add-to-cart]');
            if (addToCartBtn) {
              addToCartBtn.disabled = true;
              addToCartBtn.classList.remove('btn--primary');
              addToCartBtn.classList.add('btn--secondary');
              const btnText = addToCartBtn.querySelector('.btn__text');
              if (btnText) {
                btnText.textContent = 'Sold Out';
              }
            }
          }
        }
      } catch (err) {
        console.warn('[WishlistDrawer] Failed to fetch variant data:', err);
      }
    }
  }

  // Only the heading count inside the drawer. The header badge belongs to
  // wishlist-custom.js, which runs on every page — two writers with two definitions of
  // the number made the badge change when the drawer opened.
  updateCount(count) {
    this.countElements.forEach((el) => {
      el.textContent = count;
    });
  }

  showEmpty() {
    // A wishlist with zero items still has content to show if the customer has room
    // previews, and those can be shared on their own, so the footer stays with them.
    if (this.hasRoomPreviews) {
      this.emptyState?.setAttribute('hidden', '');
      this.bodyContainer?.removeAttribute('hidden');
      this.footer?.removeAttribute('hidden');
      this.renderProjectCardsWithTemplates();
    } else {
      this.emptyState?.removeAttribute('hidden');
      this.bodyContainer?.setAttribute('hidden', '');
      this.footer?.setAttribute('hidden', '');
    }
    this.updateCount(this.projects.length);
  }

  // One row per saved thing: projects plus the wishlist items a project isn't already
  // standing in for. The header badge and the heading both read from this.
  itemCount() {
    const wishlistOnly = this.wishlistItems.filter(
      (item) => !this.projectVariantIds.has(String(item.variant?.id || item.variantId || ''))
    );
    return this.projects.length + wishlistOnly.length;
  }

  refreshState() {
    if (this.wishlistItems.length) {
      this.showBody();
      this.updateCount(this.itemCount());
    } else {
      this.showEmpty();
    }
  }

  showBody() {
    this.emptyState?.setAttribute('hidden', '');
    this.bodyContainer?.removeAttribute('hidden');
    this.footer?.removeAttribute('hidden');
  }

  async loadProjects() {
    try {
      this.projects = (await window.LampsRoomStudio?.projects?.()) || [];
    } catch (err) {
      console.warn('[WishlistDrawer] Failed to load projects:', err);
      this.projects = [];
    }
    this.projectVariantIds = new Set(
      this.projects.map((record) => String(record.product?.variantId || '')).filter(Boolean)
    );
    this.hasRoomPreviews = this.projects.length > 0;
  }

  renderProjectCards(productCardMap = null) {
    if (!this.itemsContainer) return;

    this.roomPreviewObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.roomPreviewObjectUrls = [];
    this.itemsContainer.querySelectorAll('[data-room-preview-id]').forEach((node) => node.remove());

    const cards = this.projects
      .map((record) => this.buildRoomPreviewCard(record, productCardMap?.[record?.product?.id]))
      .filter(Boolean);
    cards.reverse().forEach((card) => this.itemsContainer.prepend(card));

    if (this.disclaimer) this.disclaimer.hidden = !this.hasRoomPreviews;
    this.updateSignInPrompt();
    this.prepareProjectShareIds();
  }

  prepareProjectShareIds() {
    const share = window.LampsRoomShare;
    if (!share?.projectShareIds || !this.projects.length) { this.projectShareIds = []; return; }
    const pending = this.projectShareIdsPromise = share.projectShareIds(this.projects)
      .then((ids) => { if (this.projectShareIdsPromise === pending) this.projectShareIds = ids; return ids; })
      .catch(() => []);
  }

  // Reached when projects render without the wishlist fetch that normally supplies the
  // card templates — an empty wishlist, or a project saved while the drawer is open.
  async renderProjectCardsWithTemplates() {
    if (!this.itemsContainer || !this.projects.length) {
      this.renderProjectCards();
      return;
    }
    let productCardMap = null;
    try {
      productCardMap = await this.fetchProductCards(this.projectProductIds());
    } catch (err) {
      console.error('[WishlistDrawer] Failed to fetch project cards:', err);
    }
    this.renderProjectCards(productCardMap);
    await this.updateCardsWithVariants();
  }

  // Asking someone to register before they have anything to lose converts badly, so the
  // prompt waits until there is a project on the line.
  updateSignInPrompt() {
    if (!this.signInPrompt) return;
    this.signInPrompt.hidden = this.signedIn || !this.hasRoomPreviews;
  }

  // A project is a wishlist row whose photo is the shopper's own room, so it starts from
  // the same Liquid card and swaps the image. Rebuilding it by hand is what lost the
  // vendor line, the option list and the sale price.
  adaptCardToProject(templateCard, record) {
    const product = record.product;
    const labels = this.roomPreviewLabels;

    const card = templateCard.cloneNode(true);
    card.dataset.roomPreviewId = record.id;
    card.dataset.roomPreviewProduct = JSON.stringify(product);
    if (product.variantId) card.dataset.variantId = product.variantId;

    const previewSrc = record.blob ? URL.createObjectURL(record.blob) : record.imageUrl;
    if (record.blob) this.roomPreviewObjectUrls.push(previewSrc);

    const media = card.querySelector('.wishlist-drawer-item__media');
    if (previewSrc && media) {
      media.classList.add('wishlist-drawer-item__media--preview');

      const image = media.querySelector('.wishlist-drawer-item__image');
      if (image) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = previewSrc;
        image.alt = product.title;
      }

      const badge = document.createElement('span');
      badge.className = 'wishlist-drawer-item__preview-badge';
      badge.textContent = labels.badge;
      media.appendChild(badge);
    }

    // Same controls, routed to the project handlers instead of the wishlist ones.
    const remove = card.querySelector('[data-wishlist-remove]');
    if (remove) {
      delete remove.dataset.wishlistRemove;
      remove.dataset.roomPreviewRemove = '';
      remove.setAttribute('aria-label', `${labels.remove} ${product.title}`);
      remove.textContent = labels.remove;
    }

    card.querySelectorAll('[data-wishlist-add-to-cart]').forEach((button) => {
      delete button.dataset.wishlistAddToCart;
      button.dataset.roomPreviewAddToCart = '';
    });

    return card;
  }

  buildRoomPreviewCard(record, templateCard) {
    const product = record?.product;
    if (!product) return null;

    if (templateCard) return this.adaptCardToProject(templateCard, record);

    const labels = this.roomPreviewLabels;

    const li = document.createElement('li');
    li.className = 'wishlist-drawer-item flex flex-col gap-4 py-4 first:pt-3 last:pb-3';
    li.dataset.roomPreviewId = record.id;
    li.dataset.roomPreviewProduct = JSON.stringify(product);

    const productRow = document.createElement('div');
    productRow.className = 'wishlist-drawer-item__product flex items-start gap-3';

    const previewSrc = record.blob ? URL.createObjectURL(record.blob) : record.imageUrl;
    if (record.blob) this.roomPreviewObjectUrls.push(previewSrc);

    if (previewSrc) {
      const mediaLink = document.createElement('a');
      mediaLink.className = 'wishlist-drawer-item__media wishlist-drawer-item__media--preview blocks-radius media-wrapper';
      mediaLink.href = product.url;
      mediaLink.tabIndex = -1;
      mediaLink.setAttribute('aria-label', product.title);

      const img = document.createElement('img');
      img.src = previewSrc;
      img.alt = product.title;
      img.width = 120;
      img.height = 120;
      img.loading = 'lazy';
      img.className = 'wishlist-drawer-item__image';
      mediaLink.appendChild(img);

      const badge = document.createElement('span');
      badge.className = 'wishlist-drawer-item__preview-badge';
      badge.textContent = labels.badge;
      mediaLink.appendChild(badge);

      productRow.appendChild(mediaLink);
    }

    const details = document.createElement('div');
    details.className = 'wishlist-drawer-item__details flex-grow flex flex-col gap-3';

    const headRow = document.createElement('div');
    headRow.className = 'flex justify-between gap-3';

    const titleCol = document.createElement('div');
    titleCol.className = 'grid flex-1';
    const titleBlock = document.createElement('div');
    titleBlock.className = 'block';
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.className = 'wishlist-drawer-item__title text-pcard-title reversed-link';
    titleLink.textContent = product.title;
    titleBlock.appendChild(titleLink);
    titleCol.appendChild(titleBlock);
    // The wishlist card carries the brand on its own line rather than leaving it to read
    // as part of the title, so a project row matches it.
    if (product.vendor) {
      const vendorEl = document.createElement('p');
      vendorEl.className = 'wishlist-drawer-item__vendor text-subtext text-sm m-0';
      vendorEl.textContent = `by ${product.vendor}`;
      titleCol.appendChild(vendorEl);
    }
    // Guarded again at the render site: a preview saved before the record carried the
    // guard still has Shopify's "Default Title" placeholder stored on it.
    if (product.variantTitle && product.variantTitle !== 'Default Title') {
      const variantEl = document.createElement('p');
      variantEl.className = 'wishlist-drawer-item__sku text-subtext text-xs m-0';
      variantEl.textContent = product.variantTitle;
      titleCol.appendChild(variantEl);
    }
    headRow.appendChild(titleCol);

    if (product.price) {
      const pricesCol = document.createElement('div');
      pricesCol.className = 'wishlist-drawer-item__prices text-right flex flex-col gap-1 shrink-0';
      const priceEl = document.createElement('span');
      priceEl.className = 'wishlist-drawer-item__price-regular';
      const moneyFormat = FoxTheme.settings?.moneyFormat || '${{amount}}';
      priceEl.textContent = FoxTheme.Currency.formatMoney(Math.round(product.price * 100), moneyFormat);
      pricesCol.appendChild(priceEl);
      headRow.appendChild(pricesCol);
    }

    details.appendChild(headRow);
    productRow.appendChild(details);
    li.appendChild(productRow);

    const actionRow = document.createElement('div');
    actionRow.className = 'wishlist-drawer-item__action w-full flex items-center justify-between gap-3';

    const textActions = document.createElement('div');
    textActions.className = 'flex items-center gap-3';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'wishlist-drawer-item__remove-link';
    removeBtn.dataset.roomPreviewRemove = '';
    removeBtn.setAttribute('aria-label', `${labels.remove} ${product.title}`);
    removeBtn.textContent = labels.remove;
    textActions.appendChild(removeBtn);

    actionRow.appendChild(textActions);

    const cartBtn = document.createElement('button');
    cartBtn.type = 'button';
    cartBtn.className = 'wishlist-drawer-item__move-cta btn btn--primary btn--small';
    cartBtn.dataset.roomPreviewAddToCart = '';
    cartBtn.textContent = labels.addToCart;
    actionRow.appendChild(cartBtn);

    li.appendChild(actionRow);

    return li;
  }

  parseRoomPreviewProduct(card) {
    try {
      return JSON.parse(card?.dataset.roomPreviewProduct || 'null');
    } catch {
      return null;
    }
  }

  handleRoomPreviewRemove(btn) {
    const card = btn.closest('[data-room-preview-id]');
    const id = card?.dataset.roomPreviewId;
    if (!id || !window.LampsRoomStudio) return Promise.resolve();

    if (card) {
      card.style.opacity = '0.5';
      card.style.pointerEvents = 'none';
    }

    return window.LampsRoomStudio.deletePreview(id).catch((err) => {
      console.error('[WishlistDrawer] Failed to remove room preview:', err);
      if (card) {
        card.style.opacity = '';
        card.style.pointerEvents = '';
      }
    });
  }

  async handleRoomPreviewAddToCart(btn) {
    if (!window.LampsRoomCommerce) return;
    const card = btn.closest('[data-room-preview-id]');
    const product = this.parseRoomPreviewProduct(card);
    if (!product) return;

    const labels = this.roomPreviewLabels;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      await window.LampsRoomCommerce.addToCart(product);
      btn.textContent = labels.cartAdded;
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      this.hide();
    } catch (err) {
      console.error('[WishlistDrawer] Failed to add room preview product to cart:', err);
      btn.textContent = labels.actionError;
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  }

  // A preview saved while the drawer is still loading would otherwise be dropped: it
  // lands after loadProjects has read the list and before initialized is set.
  async onRoomUpdate() {
    if (this.isLoading) { this.roomUpdatePending = true; return; }
    if (!this.initialized) return;
    await this.loadProjects();
    await this.renderProjectCardsWithTemplates();
    this.refreshState();
  }

  async handleClick(event) {
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    const addToCartBtn = event.target.closest('[data-wishlist-add-to-cart]');
    const shareBtn = event.target.closest('[data-wishlist-share]');
    const previewRemoveBtn = event.target.closest('[data-room-preview-remove]');
    const previewCartBtn = event.target.closest('[data-room-preview-add-to-cart]');

    if (removeBtn) {
      event.preventDefault();
      await this.handleRemove(removeBtn);
    } else if (addToCartBtn) {
      event.preventDefault();
      await this.handleAddToCart(addToCartBtn);
    } else if (shareBtn) {
      event.preventDefault();
      this.handleShare(shareBtn);
    } else if (previewRemoveBtn) {
      event.preventDefault();
      await this.handleRoomPreviewRemove(previewRemoveBtn);
    } else if (previewCartBtn) {
      event.preventDefault();
      await this.handleRoomPreviewAddToCart(previewCartBtn);
    }
  }

  async handleRemove(btn) {
    if (!window.WishlistKing) return;

    const card = btn.closest('.wishlist-drawer-item');
    const wishlistItemId = card?.dataset.wishlistItemId;

    if (!wishlistItemId) {
      console.warn('[WishlistDrawer] No wishlist item ID found for removal');
      return;
    }

    if (card) {
      card.style.opacity = '0.5';
      card.style.pointerEvents = 'none';
    }

    try {
      await window.WishlistKing.removeWishlistItem({
        wishlistItemId: wishlistItemId,
      });
      this.dispatchRemoved(wishlistItemId, card);

      // Remove from local array
      this.wishlistItems = this.wishlistItems.filter(
        (i) => i.id !== wishlistItemId
      );

      // Remove from DOM
      if (card) {
        card.remove();
      }

      // Update count and check if empty
      this.updateCount(this.itemCount());

      if (this.wishlistItems.length === 0) {
        this.showEmpty();
      }

      // Dispatch event for other components (with source to prevent self-triggering reload)
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: { count: this.wishlistItems.length, source: 'wishlist-drawer' },
        })
      );
    } catch (err) {
      console.error('[WishlistDrawer] Failed to remove item:', err);
      if (card) {
        card.style.opacity = '';
        card.style.pointerEvents = '';
      }
    }
  }

  async handleAddToCart(btn) {
    const variantId = btn.dataset.variantId;
    const productUrl = btn.dataset.productUrl;
    const hasOnlyDefaultVariant = !btn.textContent.toLowerCase().includes('choose');

    if (!variantId && !hasOnlyDefaultVariant) {
      // Navigate to product page for variant selection
      if (productUrl) {
        window.location.href = productUrl;
      }
      return;
    }

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const formData = {
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
          },
        ],
        sections: this.getSectionsToRender(),
      };

      const response = await fetch(`${FoxTheme.routes.root_url}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if request was successful
      if (!response.ok) {
        console.error('[WishlistDrawer] Add to cart failed:', response.status, response.statusText);
        throw new Error('Could not add to cart. Please try again.');
      }

      const data = await response.json();

      if (data.status === 422) {
        throw new Error(data.description || 'Could not add to cart');
      }

      // Fetch full cart data (cart/add.js only returns items added, not full cart with item_count)
      // Use the sections from /cart/add.js response which includes cart-icon-bubble with correct count
      let cartForEvent = { sections: data['sections'] };
      try {
        const cartResponse = await fetch(`${FoxTheme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
        });
        if (cartResponse.ok) {
          const cartJson = await cartResponse.json();
          cartJson['sections'] = data['sections'];
          cartForEvent = cartJson;
        }
      } catch (cartFetchError) {
        console.warn('[WishlistDrawer] Could not fetch full cart data:', cartFetchError);
      }

      // Publish cart update event
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: cartForEvent,
      });

      // Remove item from wishlist after successful add to cart
      const card = btn.closest('.wishlist-drawer-item');
      const wishlistItemId = card?.dataset.wishlistItemId;
      if (wishlistItemId && window.WishlistKing) {
        try {
          await window.WishlistKing.removeWishlistItem({ wishlistItemId });
          this.dispatchRemoved(wishlistItemId, card);
          // Remove from local array
          this.wishlistItems = this.wishlistItems.filter((i) => i.id !== wishlistItemId);
          // Remove card from DOM
          if (card) {
            card.remove();
          }
          // Update count
          this.updateCount(this.itemCount());
          // Dispatch event for other components
          document.dispatchEvent(
            new CustomEvent('wishlist:updated', {
              detail: { count: this.wishlistItems.length, source: 'wishlist-drawer' },
            })
          );
        } catch (removeErr) {
          console.warn('[WishlistDrawer] Failed to remove item from wishlist:', removeErr);
        }
      }

      // Open cart drawer
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: { open: true },
        })
      );

      // Close wishlist drawer (or show empty if no items left)
      if (this.wishlistItems.length === 0) {
        this.showEmpty();
      }
      this.hide();
    } catch (err) {
      console.error('[WishlistDrawer] Failed to add to cart:', err);
      alert(err.message || 'Failed to add to cart');
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  getSectionsToRender() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  async handleShare(btn) {
    // Room previews travel as share ids alongside the Swish list, which only knows about
    // products; either half may be missing.
    const shareIds = this.projectShareIds || [];
    if (!this.wishlistId && !shareIds.length) return;
    const shareUrl = window.LampsRoomShare?.projectsUrl
      ? window.LampsRoomShare.projectsUrl({ wishlistId: this.wishlistId, shareIds })
      : `${window.location.origin}/pages/wishlist${this.wishlistId ? `?list=${this.wishlistId}` : ''}`;

    // Check if on mobile (use native share) or desktop (use clipboard)
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const shareData = {
      title: 'My Projects',
      text: 'Lights I have saved to a project',
      url: shareUrl,
    };

    try {
      // Use native share on mobile only
      if (isMobile && navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Desktop: copy to clipboard and show feedback
        await navigator.clipboard.writeText(shareUrl);
        this.showShareFeedback(btn);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[WishlistDrawer] Share error:', error);
        // Final fallback
        try {
          await navigator.clipboard.writeText(shareUrl);
          this.showShareFeedback(btn);
        } catch (e) {
          console.error('[WishlistDrawer] Clipboard error:', e);
        }
      }
    }
  }

  showShareFeedback(btn) {
    const textSpan = btn?.querySelector('span:last-child');
    if (textSpan) {
      const originalText = textSpan.textContent;
      textSpan.textContent = 'Link Copied';
      btn.classList.add('is-clicked');
      setTimeout(() => {
        textSpan.textContent = originalText;
        btn.classList.remove('is-clicked');
      }, 2000);
    }
  }

  onWishlistUpdate(event) {
    // Ignore events we dispatched ourselves
    if (event.detail?.source === 'wishlist-drawer') {
      return;
    }

    // Refresh when wishlist is updated elsewhere
    if (this.initialized && this.open) {
      this.loadWishlistItems();
    } else {
      // Mark as needing refresh on next open
      this.initialized = false;
    }
  }

  /**
   * Tell other components (klaviyo-tracking.js) which item left the wishlist. The drawer
   * already updated itself, so onWlkUpdate skips its own event.
   */
  dispatchRemoved(wishlistItemId, card) {
    const item = this.wishlistItems.find((i) => i.id === wishlistItemId);
    document.dispatchEvent(
      new CustomEvent('wlk:wishlist:removed', {
        detail: {
          productHandle: item?.product?.handle,
          variantId: card?.dataset.variantId || item?.variant?.id || item?.variantId,
          source: 'wishlist-drawer',
        },
      })
    );
  }

  onWlkUpdate(event) {
    if (event?.detail?.source === 'wishlist-drawer') return;

    // WishlistKing native event - mark as needing refresh
    if (this.initialized && this.open) {
      this.loadWishlistItems();
    } else {
      this.initialized = false;
    }

    // Open drawer when item is added (wlk:wishlist:added event)
    if (event?.type === 'wlk:wishlist:added') {
      // Small delay to allow WishlistKing state to update
      setTimeout(() => {
        this.initialized = false;
        this.show();
      }, 100);
    }
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }

    // Refresh items if not initialized or stale
    if (!this.initialized) {
      this.initialize();
    }
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);
